import type { CSSProperties } from "react";

/**
 * Token warna panel asisten — satu-satunya tempat nilai warna panel (Drawer
 * FAB, mode tertanam di halaman Bantuan, /profile, /wall). Dipasang sebagai
 * CSS variable di akar panel (`panelSurfaceStyle`); komponen anak memakainya
 * lewat `PV.*` sehingga terang/gelap berganti tanpa `useIsDark` per komponen.
 */

export interface PanelPalette {
	/** Latar halaman dashboard di belakang panel (acuan pembeda, bukan dipakai panel). */
	page: string;
	panelTop: string;
	panelBottom: string;
	header: string;
	/** Garis tepi luar (sisi kiri panel). */
	border: string;
	/** Garis pemisah di dalam panel (header, composer, strip suara). */
	divider: string;
	text: string;
	dimmed: string;
	link: string;
	bubbleAssistantBg: string;
	bubbleAssistantText: string;
	bubbleUserBg: string;
	bubbleUserText: string;
	chipBg: string;
	chipText: string;
	chipBorder: string;
	inputBg: string;
	inputText: string;
	inputBorder: string;
	stripBg: string;
	shadow: string;
}

export const PANEL_PALETTE: Record<"dark" | "light", PanelPalette> = {
	dark: {
		page: "#11192D",
		panelTop: "#262f5c",
		panelBottom: "#1d2449",
		header: "#313b73",
		border: "#5663b3",
		divider: "#3f4a8a",
		text: "#e8ebf8",
		dimmed: "#aab3d8",
		link: "#a5bbff",
		bubbleAssistantBg: "#323c76",
		bubbleAssistantText: "#eef1fb",
		bubbleUserBg: "#3b5bdb",
		bubbleUserText: "#ffffff",
		chipBg: "#2d3668",
		chipText: "#dfe4ff",
		chipBorder: "#6a78cf",
		inputBg: "#161c3b",
		inputText: "#e8ebf8",
		inputBorder: "#5663b3",
		stripBg: "#2a3366",
		shadow: "-12px 0 32px rgba(5, 8, 25, 0.55)",
	},
	light: {
		page: "#edf3f8",
		panelTop: "#f7f9ff",
		panelBottom: "#eef3fd",
		header: "#e2eafb",
		border: "#b3c2e6",
		divider: "#cfdaf2",
		text: "#1b2440",
		dimmed: "#55607f",
		link: "#1d4ed8",
		bubbleAssistantBg: "#ffffff",
		bubbleAssistantText: "#1b2440",
		bubbleUserBg: "#2563eb",
		bubbleUserText: "#ffffff",
		chipBg: "#e6edfc",
		chipText: "#1e40af",
		chipBorder: "#aebfec",
		inputBg: "#ffffff",
		inputText: "#1b2440",
		inputBorder: "#b3c2e6",
		stripBg: "#fafcff",
		shadow: "-10px 0 28px rgba(30, 50, 110, 0.16)",
	},
};

export const panelPalette = (dark: boolean): PanelPalette =>
	PANEL_PALETTE[dark ? "dark" : "light"];

/** Gradasi halus atas → bawah latar panel. */
export const panelBackground = (p: PanelPalette): string =>
	`linear-gradient(180deg, ${p.panelTop}, ${p.panelBottom})`;

type TokenKey = Exclude<keyof PanelPalette, "page">;

const VAR_PREFIX = "--ap-";
const kebab = (k: string) => k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** Referensi `var(--ap-…)` per token, untuk dipakai komponen di dalam panel. */
export const PV = Object.fromEntries(
	(Object.keys(PANEL_PALETTE.dark) as (keyof PanelPalette)[])
		.filter((k) => k !== "page")
		.map((k) => [k, `var(${VAR_PREFIX}${kebab(k)})`]),
) as Record<TokenKey, string>;

/**
 * Gaya akar panel: latar gradasi, warna teks, CSS variable token, dan
 * override variabel Mantine (teks, dimmed, garis) agar komponen bawaan yang
 * belum memakai `PV` tetap kontras di latar baru.
 */
export function panelSurfaceStyle(dark: boolean): CSSProperties {
	const p = panelPalette(dark);
	const vars: Record<string, string> = {
		"--mantine-color-text": p.text,
		"--mantine-color-dimmed": p.dimmed,
		"--mantine-color-anchor": p.link,
		"--mantine-color-default-border": p.divider,
	};
	for (const [k, v] of Object.entries(p)) {
		if (k !== "page") vars[`${VAR_PREFIX}${kebab(k)}`] = v;
	}
	return {
		...vars,
		background: panelBackground(p),
		color: p.text,
	} as CSSProperties;
}
